import MiniEditableiCollapse from "@builder/components/app/MiniEditableiCollapse";
import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import Button from "@builder/components/general/Button";
import OliverText from "@builder/components/general/OliverText";
import SelectImage from "@builder/components/general/SelectImage";
import SelectInput from "@builder/components/general/SelectInput";
import TextInput from "@builder/components/general/TextInput";
import { getPages, getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { setCurrentPopup, setIconState } from "@builder/store/slices/popupSlice";
import { MenuLink, MenuNode, NodeStates } from "@common/build/types";
import { useState } from "react";
import { v4 } from "uuid";
import { useAppDispatch, useAppSelector } from "../../../../store/store";
import { EditableProps } from "../../editableProps";
import Typography from "@builder/components/editable/style/Typography";
import StyleIcon from "@builder/components/editable/style/StyleIcon";
import { getStyle, getStylePath } from "@builder/components/editable/helpers";
import { ReorderableList } from "@builder/components/general/ReorderableListDND/ReorderableList";
import { DragHandle } from "@builder/components/general/ReorderableListDND/DragHandle";


interface EditMenuLinkProps {
    menuLink: MenuLink;
    idx: number;
    onDelete?: () => void;
    onCopy?: () => void;
    isOpen?: boolean;
    mode?: "edit" | "typography" | "icons";
    nodeState?: NodeStates;
}

export function EditMenuLink({ menuLink, idx, onCopy, onDelete, isOpen, mode = "edit", nodeState }: EditMenuLinkProps) {
    const dispatch = useAppDispatch();
    const homepageID = useAppSelector(state => state.builder.build?.specialLayers?.homepage?.id);
    const pages = useAppSelector(getPages);
    const node = useAppSelector(getSelectedNode) as MenuNode;
    const pageOptions = Object.fromEntries(
        [{ name: "Previous page", link: "-1" }, { name: "Station Home", link: "-2" }, ...pages.map(page => ({ name: page.name, link: page.id }))].map(
            p => [p.link, p.name]
        )
    );

    const { name, link, icon } = menuLink;

    const openPopupCallback = () => {
        dispatch(setIconState(["menu", "links", idx, "icon"]));
        dispatch(setCurrentPopup("icons"));
    };

    const handleApplyAll = (path: (string | number)[]) => {
        if (!node?.menu?.links) return;
        
        const style = getStyle(node, path, nodeState);
        const stylesToApply = style.stateStyle || style.default;

        
        node.menu.links.forEach((_, linkIdx) => {
            if (linkIdx !== idx) {
                const newPath = nodeState 
                    ? path.map(segment => segment === `links[${idx}]` ? `links[${linkIdx}]` : segment)
                    : path.map(segment => segment === idx ? linkIdx : segment);
                 
                const targetPath = getStylePath(newPath, nodeState);
                dispatch(updateNodeProperty({ propertyPath: targetPath, value: stylesToApply }));
            }
        });
    };

    return (
        <MiniEditableiCollapse 
            title={name || pageOptions[link]} 
            noTranslate 
            onDelete={onDelete} 
            onCopy={onCopy} 
            open={isOpen}
        >
            {mode === "edit" && (
                <>
                    <TextInput
                        title="Menu Item Name"
                        value={name}
                        callback={t => {
                            dispatch(updateNodeProperty({ propertyPath: ["menu", "links", idx, "name"], value: t }));
                        }}
                        placeholder="Leave blank to use icon only"
                    />
                    <SelectInput
                        title="Page link"
                        layout="column"
                        value={link === "0" ? homepageID : link}
                        callback={t => {
                            dispatch(updateNodeProperty({ propertyPath: ["menu", "links", idx, "link"], value: t }));
                        }}
                        options={pageOptions}
                        className="link-page-select"
                        noTranslate
                    />
                    <SelectImage
                        type="icon"
                        callback={openPopupCallback}
                        removeCallback={() => {
                            dispatch(updateNodeProperty({ propertyPath: ["menu", "links", idx, "icon"], value: "", remove: true }));
                        }}
                        img={icon?.src}
                        label="Select Icon"
                    />
                </>
            )}
            {mode === "icons" && icon && (
                <>
                    <StyleIcon
                        noWrapper
                        path={nodeState ? ["menu", `links[${idx}]`, "icon", "style"] : ["menu", `links`, idx, "icon", "style"]}
                        stateMode={nodeState}
                        overrideIconSrc={icon.src}
                    />
                    
                        <Button 
                            text="Apply to All Menu Icons" 
                            onClick={() => {handleApplyAll(nodeState ? ["menu", `links[${idx}]`, "icon", "style"] : ["menu", `links`, idx, "icon", "style"]),
                            handleApplyAll(nodeState ? ["menu", `links[${idx}]`, "icon", "colors"] : ["menu", `links`, idx, "icon", "colors"])}
                        }
                            buttonStyle="baby-blue" 
                            size="fit-small"
                        />
                    
                </>
            )}
            {mode === "typography" && (
                <>
                    <Typography
                        noWrapper
                        path={nodeState ? ["menu", `links[${idx}]`, "typography"] : ["menu", `links`, idx, "typography"]}
                        stateMode={nodeState}
                    />
                 
                        <Button 
                            text="Apply to All Menu Links" 
                            onClick={() => handleApplyAll(nodeState ? ["menu", `links[${idx}]`, "typography"] : ["menu", `links`, idx, "typography"])} 
                            buttonStyle="baby-blue" 
                            size="fit-small"
                        />
                    
                </>
            )}
        </MiniEditableiCollapse>
    );
}

// Wrapper component to handle drag functionality
function MenuLinkWrapper({ link, index, showDragHandle, ...props }: { 
  link: MenuLink; 
  index: number; 
  showDragHandle?: boolean;
} & Omit<EditMenuLinkProps, 'menuLink' | 'idx'>) {
    return (
        <div className="menu-link-wrapper">
            {showDragHandle && <DragHandle />}
            <div className="menu-link-content">
                <EditMenuLink 
                    menuLink={link} 
                    idx={index} 
                    {...props} 
                />
            </div>
        </div>
    );
}

const EditMenu: React.FC<EditableProps> = ({ parent }: EditableProps) => {
    const dispatch = useAppDispatch();
    const node = useAppSelector(getSelectedNode) as MenuNode;
    const [openFromNowOn, setOpenFromNowOn] = useState(false);

    const handleAddLink = () => {
        setOpenFromNowOn(true);
        dispatch(
            updateNodeProperty({
                propertyPath: ["menu", "links"],
                value: [...node.menu.links, { name: "", id: v4(), link: "" }]
            })
        );
    };

    const handleDeleteLink = (index: number) => {
        const newMenu = [...node.menu.links];
        newMenu.splice(index, 1);
        dispatch(updateNodeProperty({ propertyPath: ["menu", "links"], value: newMenu }));
    };

    const handleCopyLink = (index: number) => {
        const newMenu = [...node.menu.links];
        newMenu.splice(index, 0, { ...newMenu[index], id: v4() });
        dispatch(updateNodeProperty({ propertyPath: ["menu", "links"], value: newMenu }));
    };

    const handleReorderLinks = (newLinks: MenuLink[]) => {
        dispatch(updateNodeProperty({ propertyPath: ["menu", "links"], value: newLinks }));
    };

    return (
        <SidebarCollapse title="Menu" parent={parent}>
            <OliverText text="Menu Links" size="xs" translatePrefix="builderElements." />
            
            <ReorderableList
                items={node.menu.links}
                onReorder={handleReorderLinks}
                itemKey={(link) => link.id}
                showDragHandle={true}
                renderItem={(link, index, dragProps) => (
                    <MenuLinkWrapper
                        link={link}
                        index={index}
                        onCopy={() => handleCopyLink(index)}
                        onDelete={() => handleDeleteLink(index)}
                        isOpen={openFromNowOn}
                        showDragHandle={dragProps.showDragHandle}
                    />
                )}
            />

            <Button text="Add link" onClick={handleAddLink} buttonStyle="outline" size="small" style={{ border: "1px solid var(--blue)" }} />
        </SidebarCollapse>
    );
};

export default EditMenu;
